import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { DocumentSearch } from '../components/DocumentSearch.js';
import { api, ApiError } from '../api/client.js';
import type { LakeDocument, LakeStats, LakeTree, LandResponse, TreeHop } from '../api/client.js';
import { useAuth } from '../state/auth.js';
import { useT } from '../state/i18n.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import {
  collectDropped,
  filesFromInput,
  DIRECT_BYTE_CAP,
  formatBytes,
  isJunkPath,
  planBatches,
  runPool,
  sha256Hex,
  type LakeFile,
} from '../lib/lake-upload.js';
import '../styles/smartlake.css';

// Smartlake — "뭐든 던져 넣는 곳". 파일·폴더를 얼마든지 받아 해시 dedupe 로 불변 저장하고,
// 기본 서술자(text.page·doc.meta)가 붙는 원천층의 웹 표면. 위에서 JevRAG 같은 소비자가 선택지를 만든다.
//
// 이 화면이 하는 일 셋: (1) 던지기 — 폴더 재귀·해시 사전확인·요청 분할·동시 업로드·파일별 결과,
// (2) 훑어보기 — 스코프 통계와 필터 가능한 목록, (3) 한 문서 들여다보기 — 서술자·원장·원문 미리보기.
// 서버 계약: POST /documents/land, GET /documents, GET /documents/stats, POST /documents/exists,
//           GET /proposals/:id/descriptors(/summary), GET /proposals/:id/jobs, POST .../jobs/baseline/run.
// 언어: 유저 노출 문자열은 전부 t({ko,en}).

type ItemStatus =
  | 'pending'
  | 'junk'
  | 'oversize'
  | 'hashing'
  | 'exists'
  | 'uploading'
  | 'landed'
  | 'deduplicated'
  | 'failed';

interface QueueItem {
  id: string;
  lf: LakeFile;
  status: ItemStatus;
  hash?: string;
  message?: string;
  proposalId?: string;
  baseline?: string;
}

interface Filters {
  q: string;
  type: string;
  source: string;
  batch: string;
  baseline: string;
  descriptor: string; // '' | 'has:<kind>' | 'missing:<kind>'
  node: string; // 트리 노드 id (서브트리)
}

const EMPTY_FILTERS: Filters = { q: '', type: '', source: '', batch: '', baseline: '', descriptor: '', node: '' };
const PAGE_SIZE = 50;
const HASH_CONCURRENCY = 4;
const UPLOAD_CONCURRENCY = 2;

let seq = 0;
function nextId(): string {
  seq += 1;
  return 'q' + seq.toString(36) + '_' + Date.now().toString(36);
}

function newBatchId(): string {
  return 'batch_web_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function msgOf(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function shortHash(h: string | null | undefined): string {
  return h ? h.slice(0, 12) : '—';
}

function shortDate(v: string | null | undefined): string {
  return v ? String(v).slice(0, 16).replace('T', ' ') : '—';
}

function parseMetadata(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function SmartlakePage(): JSX.Element {
  const auth = useAuth();
  const t = useT();
  useDocumentMeta({
    title: t({ ko: 'Smartlake — CloudBTL', en: 'Smartlake — CloudBTL' }),
    description: t({
      ko: '파일과 폴더를 출처·경로·해시·디스크립터와 함께 보관하고 논리 트리로 탐색합니다.',
      en: 'Land files and folders with source paths, hashes and descriptors, then browse them as logical trees.',
    }),
    canonical: '/lake',
  });

  // ── 업로드 큐 ──
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const [source, setSource] = useState('web');
  const [metadataText, setMetadataText] = useState('');
  const [preHash, setPreHash] = useState(true);
  const [runBaseline, setRunBaseline] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lastBatch, setLastBatch] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const dirInput = useRef<HTMLInputElement | null>(null);
  const itemsRef = useRef<QueueItem[]>([]);
  itemsRef.current = items;

  // ── 라이브러리 ──
  const [stats, setStats] = useState<LakeStats | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [qDraft, setQDraft] = useState('');
  const [docs, setDocs] = useState<LakeDocument[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LakeDocument | null>(null);
  const [draining, setDraining] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const listSeq = useRef(0);

  const patchItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = useCallback((files: LakeFile[]) => {
    if (!files.length) return;
    setUploadError(null);
    setItems((prev) => {
      const seen = new Set(prev.map((p) => p.lf.path + '\u0000' + p.lf.size));
      const fresh: QueueItem[] = [];
      for (const lf of files) {
        const key = lf.path + '\u0000' + lf.size;
        if (seen.has(key)) continue;
        seen.add(key);
        fresh.push({ id: nextId(), lf, status: isJunkPath(lf.path) ? 'junk' : 'pending' });
      }
      return prev.concat(fresh);
    });
  }, []);

  const onDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      try {
        addFiles(await collectDropped(e.dataTransfer));
      } catch (err) {
        setUploadError(msgOf(err, t({ ko: '드롭한 항목을 읽지 못했습니다.', en: 'Could not read the dropped items.' })));
      }
    },
    [addFiles, t],
  );

  const onPick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) addFiles(filesFromInput(e.target.files));
      e.target.value = '';
    },
    [addFiles],
  );

  const loadStats = useCallback(async () => {
    try {
      setStats(await api.lakeStats());
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '통계를 불러오지 못했습니다.', en: 'Could not load stats.' })));
    }
  }, [t]);

  const loadDocs = useCallback(
    async (f: Filters, cursor: string | null) => {
      const my = ++listSeq.current;
      setListLoading(true);
      setListError(null);
      try {
        const params: Record<string, string | number | undefined> = {
          q: f.q || undefined,
          type: f.type || undefined,
          source: f.source || undefined,
          batch: f.batch || undefined,
          baseline: f.baseline || undefined,
          node: f.node || undefined,
          limit: PAGE_SIZE,
          cursor: cursor ?? undefined,
        };
        if (f.descriptor.startsWith('has:')) params.kind = f.descriptor.slice(4);
        if (f.descriptor.startsWith('missing:')) params.missingKind = f.descriptor.slice(8);
        const res = await api.lakeDocuments(params);
        if (my !== listSeq.current) return;
        setDocs((prev) => (cursor ? prev.concat(res.documents) : res.documents));
        setNextCursor(res.nextCursor);
      } catch (err) {
        if (my !== listSeq.current) return;
        setListError(msgOf(err, t({ ko: '목록을 불러오지 못했습니다.', en: 'Could not load documents.' })));
      } finally {
        if (my === listSeq.current) setListLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (auth.loading || !auth.user) return;
    void loadStats();
    void loadDocs(filters, null);
    // filters 변경은 applyFilters 에서 명시적으로 다시 부른다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading, auth.user?.id]);

  const applyFilters = useCallback(
    (next: Partial<Filters>) => {
      const f = { ...filters, ...next };
      setFilters(f);
      setSelected(null);
      void loadDocs(f, null);
    },
    [filters, loadDocs],
  );

  // ── 업로드 실행 ──
  const startUpload = useCallback(async () => {
    const metadata = parseMetadata(metadataText);
    if (!metadata) {
      setUploadError(t({ ko: '메타데이터는 JSON 객체여야 합니다.', en: 'Metadata must be a JSON object.' }));
      return;
    }
    const todo = itemsRef.current.filter((it) => it.status === 'pending' || it.status === 'failed');
    if (!todo.length) return;
    setRunning(true);
    setUploadError(null);
    const batchId = newBatchId();
    setLastBatch(batchId);
    try {
      // 1) 해시 → 이미 있는 것은 올리지 않는다.
      let candidates = todo;
      if (preHash) {
        await runPool(todo, HASH_CONCURRENCY, async (it) => {
          if (it.hash) return;
          patchItem(it.id, { status: 'hashing' });
          try {
            const hash = await sha256Hex(it.lf.file);
            it.hash = hash;
            patchItem(it.id, { hash, status: 'pending' });
          } catch (err) {
            patchItem(it.id, { status: 'failed', message: msgOf(err, 'hash failed') });
          }
        });
        const hashed = todo.filter((it) => it.hash);
        const existing: Record<string, { id: string; title: string }> = {};
        for (let i = 0; i < hashed.length; i += 500) {
          const chunk = hashed.slice(i, i + 500).map((it) => it.hash as string);
          try {
            Object.assign(existing, (await api.lakeExists(chunk)).existing);
          } catch {
            // 사전확인 실패는 치명적이지 않다 — 서버 dedupe 가 어차피 잡는다.
          }
        }
        candidates = [];
        for (const it of hashed) {
          const hit = existing[it.hash as string];
          if (hit) patchItem(it.id, { status: 'exists', proposalId: hit.id, message: hit.title });
          else candidates.push(it);
        }
      }
      // 2) 요청 단위로 나눠 올린다.
      const plan = planBatches(
        candidates.map((it) => ({ size: it.lf.size, it })),
      );
      // 30MB 를 넘는 파일은 GCS 세션으로 직접 올린다(2GiB 까지). 그보다 크면 너무 큼.
      const direct = plan.oversize.filter((o) => o.it.lf.size <= DIRECT_BYTE_CAP);
      for (const o of plan.oversize) if (o.it.lf.size > DIRECT_BYTE_CAP) patchItem(o.it.id, { status: 'oversize' });
      await runPool(plan.batches, UPLOAD_CONCURRENCY, async (batch) => {
        const form = new FormData();
        for (const b of batch) {
          form.append('files', b.it.lf.file, b.it.lf.file.name);
          patchItem(b.it.id, { status: 'uploading', message: undefined });
        }
        form.set('sourceRefs', JSON.stringify(batch.map((b) => b.it.lf.path)));
        form.set('source', source.trim() || 'web');
        form.set('metadata', JSON.stringify(metadata));
        form.set('ingestBatch', batchId);
        form.set('linkMode', 'none');
        form.set('dedupe', 'true');
        form.set('runBaseline', runBaseline ? 'true' : 'false');
        let res: LandResponse;
        try {
          res = await api.landDocuments(form);
        } catch (err) {
          const m = msgOf(err, t({ ko: '요청 실패', en: 'Request failed' }));
          for (const b of batch) patchItem(b.it.id, { status: 'failed', message: m });
          return;
        }
        batch.forEach((b, i) => {
          const r = res.results[i];
          if (!r) return patchItem(b.it.id, { status: 'failed', message: 'no result' });
          if (!r.ok) return patchItem(b.it.id, { status: 'failed', message: r.error ?? 'failed' });
          const base = r.baseline
            ? r.baseline.status +
              (r.baseline.pageCount !== undefined ? ' · ' + String(r.baseline.pageCount) + 'p' : '') +
              (r.baseline.reason ? ' · ' + r.baseline.reason : '')
            : undefined;
          patchItem(b.it.id, {
            status: r.deduplicated ? 'deduplicated' : 'landed',
            proposalId: r.proposal?.id,
            baseline: base,
            message: undefined,
          });
        });
      });
      // 3) 직접 업로드 — 하나씩(브라우저 메모리·회선 고려).
      await runPool(direct, 1, async (o) => {
        const it = o.it;
        patchItem(it.id, { status: 'uploading', message: t({ ko: '직접 업로드(GCS)…', en: 'Direct upload (GCS)…' }) });
        try {
          const ticket = await api.landInit(it.lf.file.name, it.lf.size);
          const put = await fetch(ticket.uploadUrl, { method: 'PUT', body: it.lf.file, headers: { 'Content-Type': 'application/octet-stream' } });
          if (!put.ok) throw new Error('PUT ' + String(put.status));
          const r = await api.landCommit({ uploadId: ticket.uploadId, source: source.trim() || 'web', sourceRef: it.lf.path, metadata, ingestBatch: batchId, runBaseline });
          const base = r.baseline
            ? r.baseline.status + (r.baseline.pageCount !== undefined ? ' · ' + String(r.baseline.pageCount) + 'p' : '') + (r.baseline.reason ? ' · ' + r.baseline.reason : '')
            : undefined;
          patchItem(it.id, { status: r.deduplicated ? 'deduplicated' : 'landed', proposalId: r.proposal?.id, baseline: base, message: undefined });
        } catch (err) {
          patchItem(it.id, { status: 'failed', message: msgOf(err, t({ ko: '직접 업로드 실패', en: 'Direct upload failed' })) });
        }
      });
    } finally {
      setRunning(false);
      void loadStats();
      void loadDocs(filters, null);
    }
  }, [metadataText, preHash, runBaseline, source, patchItem, loadStats, loadDocs, filters, t]);

  const clearDone = useCallback(() => {
    setItems((prev) => prev.filter((it) => it.status === 'pending' || it.status === 'failed' || it.status === 'uploading' || it.status === 'hashing'));
  }, []);
  const clearAll = useCallback(() => {
    if (running) return;
    setItems([]);
    setLastBatch(null);
  }, [running]);

  const counts = useMemo(() => {
    const c: Record<ItemStatus, number> = {
      pending: 0, junk: 0, oversize: 0, hashing: 0, exists: 0, uploading: 0, landed: 0, deduplicated: 0, failed: 0,
    };
    let bytes = 0;
    for (const it of items) {
      c[it.status] += 1;
      if (it.status !== 'junk') bytes += it.lf.size;
    }
    return { ...c, bytes, total: items.length };
  }, [items]);
  const doneCount = counts.landed + counts.deduplicated + counts.exists + counts.failed + counts.oversize;
  const workCount = counts.total - counts.junk;
  const progressPct = workCount ? Math.round((doneCount / workCount) * 100) : 0;

  const drain = useCallback(async () => {
    setDraining(true);
    setBanner(null);
    try {
      const r = await api.drainPipeline(50);
      setBanner(t({ ko: '대기 중 추출 처리: ', en: 'Processed queued extractions: ' }) + String(r.processed ?? 0));
      void loadStats();
      void loadDocs(filters, null);
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '처리에 실패했습니다.', en: 'Drain failed.' })));
    } finally {
      setDraining(false);
    }
  }, [t, loadStats, loadDocs, filters]);

  const STATUS_LABEL: Record<ItemStatus, string> = {
    pending: t({ ko: '대기', en: 'Queued' }),
    junk: t({ ko: '건너뜀(부산물)', en: 'Skipped (junk)' }),
    oversize: t({ ko: '너무 큼(2GB 초과)', en: 'Too large (>2GB)' }),
    hashing: t({ ko: '해시 계산', en: 'Hashing' }),
    exists: t({ ko: '이미 있음', en: 'Already there' }),
    uploading: t({ ko: '올리는 중', en: 'Uploading' }),
    landed: t({ ko: '새로 저장', en: 'Landed' }),
    deduplicated: t({ ko: '중복(서버)', en: 'Duplicate' }),
    failed: t({ ko: '실패', en: 'Failed' }),
  };
  const STATUS_CLASS: Record<ItemStatus, string> = {
    pending: 'ds-badge--neutral',
    junk: 'ds-badge--neutral',
    oversize: 'ds-badge--warning',
    hashing: 'ds-badge--accent',
    exists: 'ds-badge--neutral',
    uploading: 'ds-badge--accent',
    landed: 'ds-badge--success',
    deduplicated: 'ds-badge--neutral',
    failed: 'ds-badge--danger',
  };

  // ── 게이트 ──
  if (auth.loading) {
    return (
      <Shell>
        <section className="lake">
          <p className="muted">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
        </section>
      </Shell>
    );
  }
  if (!auth.user) {
    return (
      <Shell>
        <section className="lake">
          <h1>Smartlake</h1>
          <p className="muted">
            {t({ ko: '던져 넣으려면 로그인이 필요합니다.', en: 'Sign in to throw documents in.' })}{' '}
            <Link to="/login" className="cta-secondary">
              {t({ ko: '로그인', en: 'Sign in' })}
            </Link>
          </p>
        </section>
      </Shell>
    );
  }

  const kinds = stats ? Array.from(new Set(stats.descriptors.map((d) => d.kind))).sort() : [];
  const pendingCount = counts.pending + counts.failed;

  return (
    <Shell>
      <section className="lake">
        <header className="lake-head">
          <div>
            <h1>Smartlake</h1>
            <p className="muted">
              {t({
                ko: '뭐든 던져 넣는 곳. 같은 파일은 한 번만 저장하고, 본문 처리가 끝나면 내용으로 검색할 수 있습니다. 큰 파일 등 일부 자료는 별도 추출이 필요합니다.',
                en: 'Throw anything in. Identical files are stored once and become searchable as text extraction completes. Large files and some formats need separate extraction.',
              })}
            </p>
          </div>
          {stats ? (
            <div className="lake-stats" aria-label={t({ ko: '레이크 통계', en: 'Lake stats' })}>
              <Stat label={t({ ko: '문서', en: 'Documents' })} value={stats.total.toLocaleString()} />
              <Stat
                label={t({ ko: '용량', en: 'Size' })}
                value={formatBytes(stats.bytes)}
                hint={stats.storedBytes !== undefined && stats.storedBytes !== stats.bytes ? t({ ko: '저장 ', en: 'stored ' }) + formatBytes(stats.storedBytes) : undefined}
              />
              <Stat
                label={t({ ko: '텍스트 추출', en: 'Text extracted' })}
                value={String(stats.baseline.succeeded ?? 0)}
                hint={
                  (stats.baseline.skipped ? String(stats.baseline.skipped) + ' ' + t({ ko: '건너뜀', en: 'skipped' }) : '') +
                  (stats.baseline.failed ? ' · ' + String(stats.baseline.failed) + ' ' + t({ ko: '실패', en: 'failed' }) : '') +
                  (stats.baseline.queued ? ' · ' + String(stats.baseline.queued) + ' ' + t({ ko: '대기', en: 'queued' }) : '')
                }
              />
              <Stat label={t({ ko: '서술자 종류', en: 'Descriptor kinds' })} value={String(kinds.length)} hint={kinds.slice(0, 4).join(', ')} />
              {(stats.baseline.queued ?? 0) > 0 ? (
                <button type="button" className="ds-button ds-button--secondary ds-button--sm" onClick={drain} disabled={draining}>
                  {draining ? t({ ko: '처리 중…', en: 'Draining…' }) : t({ ko: '대기 추출 처리', en: 'Run queued' })}
                </button>
              ) : null}
            </div>
          ) : null}
        </header>
        {banner ? (
          <p className="ds-alert ds-alert--info lake-banner" role="status">
            {banner}
          </p>
        ) : null}

        {/* ── 던지기 ── */}
        <div
          className={'lake-drop' + (dragging ? ' is-dragging' : '')}
          role="button"
          tabIndex={0}
          aria-label={t({ ko: '파일이나 폴더를 끌어다 놓기', en: 'Drop files or folders' })}
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInput.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragging(false);
          }}
          onDrop={onDrop}
        >
          <input ref={fileInput} type="file" multiple hidden onChange={onPick} data-testid="lake-file-input" />
          <input
            ref={dirInput}
            type="file"
            hidden
            onChange={onPick}
            data-testid="lake-dir-input"
            // @ts-expect-error — 비표준 속성이지만 모든 주요 브라우저가 폴더 선택으로 인식한다.
            webkitdirectory=""
          />
          <div className="lake-drop__text">
            <strong>
              {dragging
                ? t({ ko: '놓으세요', en: 'Drop it' })
                : t({ ko: '파일·폴더를 여기 끌어다 놓거나 클릭', en: 'Drag files or folders here, or click' })}
            </strong>
            <span className="muted small">
              {t({
                ko: '개수 제한 없음 · 파일당 2GB · 같은 내용은 한 번만 저장',
                en: 'Any count · 2GB per file · identical bytes stored once',
              })}
            </span>
          </div>
          <button
            type="button"
            className="ds-button ds-button--secondary ds-button--sm"
            onClick={(e) => {
              e.stopPropagation();
              dirInput.current?.click();
            }}
          >
            {t({ ko: '폴더 선택', en: 'Choose folder' })}
          </button>
        </div>

        <div className="lake-controls">
          <button
            type="button"
            className="ds-button ds-button--primary"
            disabled={running || pendingCount === 0}
            onClick={() => void startUpload()}
          >
            {running
              ? t({ ko: '올리는 중… ', en: 'Uploading… ' }) + String(progressPct) + '%'
              : t({ ko: '던지기', en: 'Throw in' }) + (pendingCount ? ' (' + String(pendingCount) + ')' : '')}
          </button>
          <button type="button" className="ds-button ds-button--ghost ds-button--sm" onClick={() => setShowOptions((v) => !v)}>
            {showOptions ? t({ ko: '옵션 닫기', en: 'Hide options' }) : t({ ko: '옵션', en: 'Options' })}
          </button>
          {counts.total ? (
            <>
              <button type="button" className="ds-button ds-button--ghost ds-button--sm" onClick={clearDone} disabled={running}>
                {t({ ko: '완료 항목 지우기', en: 'Clear finished' })}
              </button>
              <button type="button" className="ds-button ds-button--ghost ds-button--sm" onClick={clearAll} disabled={running}>
                {t({ ko: '전부 지우기', en: 'Clear all' })}
              </button>
            </>
          ) : null}
          {lastBatch ? (
            <span className="muted small">
              {t({ ko: '묶음', en: 'Batch' })}: <code>{lastBatch}</code>
            </span>
          ) : null}
        </div>
        {uploadError ? (
          <p className="status err" role="alert">
            {uploadError}
          </p>
        ) : null}

        {showOptions ? (
          <div className="lake-options ds-card ds-card--muted ds-card--sm">
            <label className="ds-field">
              <span className="ds-field__label">{t({ ko: '출처(source)', en: 'Source' })}</span>
              <input className="ds-control" value={source} onChange={(e) => setSource(e.target.value)} placeholder="web" />
              <span className="ds-field__hint">
                {t({ ko: '어디서 왔는지 — onedrive, slack, scan… 필터에 쓰입니다.', en: 'Where it came from — onedrive, slack, scan… used as a filter.' })}
              </span>
            </label>
            <label className="ds-field">
              <span className="ds-field__label">{t({ ko: '메타데이터(JSON)', en: 'Metadata (JSON)' })}</span>
              <textarea
                className="ds-control"
                rows={3}
                value={metadataText}
                onChange={(e) => setMetadataText(e.target.value)}
                placeholder={'{"division": "LM", "year": 2025}'}
                spellCheck={false}
              />
              <span className="ds-field__hint">
                {t({ ko: '이 묶음의 모든 파일에 붙습니다. 키 의미는 당신 것 — 여기서는 해석하지 않습니다.', en: 'Attached to every file in this batch. Keys mean whatever you decide — not interpreted here.' })}
              </span>
            </label>
            <label className="ds-check">
              <input type="checkbox" checked={preHash} onChange={(e) => setPreHash(e.target.checked)} />
              <span>{t({ ko: '올리기 전에 해시로 이미 있는 파일 건너뛰기', en: 'Hash first and skip files already in the lake' })}</span>
            </label>
            <label className="ds-check">
              <input type="checkbox" checked={runBaseline} onChange={(e) => setRunBaseline(e.target.checked)} />
              <span>{t({ ko: '기본 텍스트 추출을 요청 안에서 바로 실행', en: 'Run baseline text extraction inline' })}</span>
            </label>
          </div>
        ) : null}

        {counts.total ? (
          <div className="lake-queue">
            <div className="lake-queue__summary">
              <span>
                {String(counts.total)} {t({ ko: '개', en: 'files' })} · {formatBytes(counts.bytes)}
              </span>
              {counts.landed ? <span className="ds-badge ds-badge--success">{STATUS_LABEL.landed} {counts.landed}</span> : null}
              {counts.deduplicated + counts.exists ? (
                <span className="ds-badge ds-badge--neutral">{t({ ko: '중복', en: 'Duplicates' })} {counts.deduplicated + counts.exists}</span>
              ) : null}
              {counts.failed ? <span className="ds-badge ds-badge--danger">{STATUS_LABEL.failed} {counts.failed}</span> : null}
              {counts.oversize ? <span className="ds-badge ds-badge--warning">{STATUS_LABEL.oversize} {counts.oversize}</span> : null}
              {counts.junk ? <span className="ds-badge ds-badge--neutral">{STATUS_LABEL.junk} {counts.junk}</span> : null}
              {running ? (
                <progress className="lake-progress" max={workCount} value={doneCount} aria-label={t({ ko: '진행률', en: 'Progress' })} />
              ) : null}
            </div>
            <div className="lake-queue__list" role="list">
              {items.map((it) => (
                <div key={it.id} className={'lake-queue__row is-' + it.status} role="listitem">
                  <span className="lake-queue__path" title={it.lf.path}>
                    {it.lf.path}
                  </span>
                  <span className="muted small">{formatBytes(it.lf.size)}</span>
                  <span className={'ds-badge ' + STATUS_CLASS[it.status]}>{STATUS_LABEL[it.status]}</span>
                  <span className="muted small lake-queue__msg" title={it.message ?? it.baseline ?? ''}>
                    {it.status === 'failed' ? it.message : it.baseline ?? (it.status === 'exists' ? it.message : '')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <DocumentSearch uploaded={items.filter(it => it.proposalId).map(it => ({ id: it.proposalId!, name: it.lf.path }))} />
        {/* ── 훑어보기 ── */}
        <div className="lake-library">
          <TreeNav selectedNode={filters.node || null} onSelect={(id) => applyFilters({ node: id ?? '' })} />
          <div className="lake-filters" role="search">
            <input
              className="ds-control"
              value={qDraft}
              placeholder={t({ ko: '제목·파일명·경로 검색', en: 'Search title, filename, path' })}
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyFilters({ q: qDraft });
              }}
              aria-label={t({ ko: '검색', en: 'Search' })}
            />
            <select className="ds-control" value={filters.type} onChange={(e) => applyFilters({ type: e.target.value })} aria-label={t({ ko: '타입', en: 'Type' })}>
              <option value="">{t({ ko: '모든 타입', en: 'All types' })}</option>
              {['pdf', 'pptx', 'docx', 'xlsx', 'html', 'md', 'file'].map((k) => (
                <option key={k} value={k}>
                  {k.toUpperCase()}
                  {stats?.byType[k] ? ' (' + String(stats.byType[k]!.docs) + ')' : ''}
                </option>
              ))}
            </select>
            <select className="ds-control" value={filters.source} onChange={(e) => applyFilters({ source: e.target.value })} aria-label={t({ ko: '출처', en: 'Source' })}>
              <option value="">{t({ ko: '모든 출처', en: 'All sources' })}</option>
              {(stats?.bySource ?? [])
                .filter((s) => s.source)
                .map((s) => (
                  <option key={s.source as string} value={s.source as string}>
                    {s.source} ({s.docs})
                  </option>
                ))}
            </select>
            <select className="ds-control" value={filters.batch} onChange={(e) => applyFilters({ batch: e.target.value })} aria-label={t({ ko: '묶음', en: 'Batch' })}>
              <option value="">{t({ ko: '모든 묶음', en: 'All batches' })}</option>
              {(stats?.batches ?? [])
                .filter((b) => b.ingestBatch)
                .map((b) => (
                  <option key={b.ingestBatch as string} value={b.ingestBatch as string}>
                    {shortDate(b.lastAt)} · {b.ingestBatch} ({b.docs})
                  </option>
                ))}
            </select>
            <select className="ds-control" value={filters.baseline} onChange={(e) => applyFilters({ baseline: e.target.value })} aria-label={t({ ko: '추출 상태', en: 'Extraction' })}>
              <option value="">{t({ ko: '추출 상태 전체', en: 'Any extraction state' })}</option>
              <option value="succeeded">{t({ ko: '텍스트 있음', en: 'Text extracted' })}</option>
              <option value="skipped">{t({ ko: '추출 건너뜀', en: 'Extraction skipped' })}</option>
              <option value="failed">{t({ ko: '실패', en: 'Failed' })}</option>
              <option value="queued">{t({ ko: '대기', en: 'Queued' })}</option>
              <option value="none">{t({ ko: '기록 없음', en: 'No record' })}</option>
            </select>
            <select className="ds-control" value={filters.descriptor} onChange={(e) => applyFilters({ descriptor: e.target.value })} aria-label={t({ ko: '서술자', en: 'Descriptor' })}>
              <option value="">{t({ ko: '서술자 전체', en: 'Any descriptor' })}</option>
              {kinds.map((k) => (
                <option key={'has:' + k} value={'has:' + k}>
                  {t({ ko: '있음: ', en: 'has: ' })}{k}
                </option>
              ))}
              {kinds.map((k) => (
                <option key={'missing:' + k} value={'missing:' + k}>
                  {t({ ko: '없음: ', en: 'missing: ' })}{k}
                </option>
              ))}
            </select>
            {JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS) ? (
              <button
                type="button"
                className="ds-button ds-button--ghost ds-button--sm"
                onClick={() => {
                  setQDraft('');
                  applyFilters(EMPTY_FILTERS);
                }}
              >
                {t({ ko: '필터 초기화', en: 'Reset' })}
              </button>
            ) : null}
          </div>
          {listError ? (
            <p className="status err" role="alert">
              {listError}
            </p>
          ) : null}
          <div className="lake-split">
            <div className="lake-list" role="table" aria-label={t({ ko: '문서 목록', en: 'Documents' })}>
              <div className="lake-row lake-row--head" role="row">
                <span>{t({ ko: '문서', en: 'Document' })}</span>
                <span>{t({ ko: '타입', en: 'Type' })}</span>
                <span>{t({ ko: '크기', en: 'Size' })}</span>
                <span>{t({ ko: '출처 / 경로', en: 'Source / path' })}</span>
                <span>{t({ ko: '추출', en: 'Extraction' })}</span>
                <span>{t({ ko: '서술자', en: 'Descriptors' })}</span>
                <span>{t({ ko: '들어온 때', en: 'Landed' })}</span>
              </div>
              {docs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={'lake-row' + (selected?.id === d.id ? ' is-selected' : '')}
                  role="row"
                  onClick={() => setSelected(d)}
                >
                  <span className="lake-row__title" title={d.originalFilename}>
                    {d.title}
                    {d.version > 1 ? <span className="ds-badge ds-badge--accent">v{d.version}</span> : null}
                    {d.storageMode === 'linked' ? <span className="ds-badge ds-badge--neutral" title={t({ ko: '연결 문서 — 바이트는 원본에', en: 'Linked — bytes at the source' })}>link</span> : null}
                  </span>
                  <span className="muted small">{d.documentType.toUpperCase()}</span>
                  <span className="muted small">{formatBytes(d.fileSize)}</span>
                  <span className="muted small lake-row__ref" title={(d.source ?? '') + ' ' + (d.sourceRef ?? '')}>
                    {d.source ? <code>{d.source}</code> : null} {d.sourceRef}
                  </span>
                  <span>
                    <BaselineBadge b={d.baseline} />
                  </span>
                  <span className="muted small">
                    {d.descriptors.length
                      ? d.descriptors.map((x) => x.kind + (x.count > 1 ? '×' + String(x.count) : '')).join(', ')
                      : '—'}
                  </span>
                  <span className="muted small">{shortDate(d.createdAt)}</span>
                </button>
              ))}
              {!docs.length && !listLoading ? (
                <p className="muted lake-empty">
                  {stats && stats.total === 0
                    ? t({ ko: '아직 비어 있습니다. 위에 던져 넣으세요.', en: 'Empty so far. Throw something in above.' })
                    : t({ ko: '조건에 맞는 문서가 없습니다.', en: 'No documents match.' })}
                </p>
              ) : null}
              {listLoading ? <p className="muted lake-empty">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p> : null}
              {nextCursor && !listLoading ? (
                <button type="button" className="ds-button ds-button--secondary ds-button--sm lake-more" onClick={() => void loadDocs(filters, nextCursor)}>
                  {t({ ko: '더 보기', en: 'Load more' })}
                </button>
              ) : null}
            </div>
            {selected ? (
              <DocDetail
                doc={selected}
                onClose={() => setSelected(null)}
                onChanged={() => {
                  void loadStats();
                  void loadDocs(filters, null);
                }}
              />
            ) : null}
          </div>
        </div>
      </section>
    </Shell>
  );
}

// ── 선택지 트리 — 결정 모델이 걷는 것과 같은 홉을 사람이 걷는다. 노드를 고르면 라이브러리가 그 서브트리로 좁혀진다. ──
function TreeNav({
  selectedNode,
  onSelect,
}: {
  selectedNode: string | null;
  onSelect: (nodeId: string | null) => void;
}): JSX.Element | null {
  const t = useT();
  const [trees, setTrees] = useState<LakeTree[] | null>(null);
  const [treeKey, setTreeKey] = useState<string>('folders');
  const [hop, setHop] = useState<TreeHop | null>(null);
  const [at, setAt] = useState<string>('root');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .trees()
      .then((r) => setTrees(r.trees))
      .catch(() => setTrees([]));
  }, []);

  useEffect(() => {
    if (!trees || !trees.length) return;
    const tree = trees.find((x) => x.key === treeKey) ?? trees[0]!;
    if (tree.key !== treeKey) setTreeKey(tree.key);
    let alive = true;
    api
      .treeNode(tree.key, at, 60)
      .then((r) => {
        if (!alive) return;
        setHop(r);
        setError(null);
      })
      .catch((err) => alive && setError(msgOf(err, t({ ko: '트리를 불러오지 못했습니다.', en: 'Could not load the tree.' }))));
    return () => {
      alive = false;
    };
  }, [trees, treeKey, at, t]);

  const go = (nodeId: string) => {
    setAt(nodeId);
    onSelect(nodeId === 'root' ? null : nodeId);
  };

  const rebuild = async () => {
    setBusy(true);
    try {
      const r = await api.rebuildFoldersTree();
      const c = await api.backfillCards(200);
      setError(t({ ko: '폴더 트리 재구축: ', en: 'Rebuilt folders: ' }) + String(r.attached) + ' · ' + t({ ko: '카드 백필: ', en: 'cards backfilled: ' }) + String(c.processed));
      setTrees(null);
      setAt('root');
      onSelect(null);
      api.trees().then((x) => setTrees(x.trees)).catch(() => setTrees([]));
    } catch (err) {
      setError(msgOf(err, t({ ko: '재구축 실패', en: 'Rebuild failed' })));
    } finally {
      setBusy(false);
    }
  };

  if (trees === null) return null;
  if (!trees.length) {
    return (
      <div className="lake-tree ds-card ds-card--muted ds-card--sm">
        <span className="muted small">
          {t({ ko: '아직 트리가 없습니다. 새로 던지면 폴더 트리가 생기고, 이미 있는 문서는 재구축으로 붙입니다.', en: 'No trees yet. New landings build the folders tree; existing documents attach on rebuild.' })}
        </span>
        <button type="button" className="ds-button ds-button--secondary ds-button--sm" onClick={() => void rebuild()} disabled={busy}>
          {busy ? t({ ko: '재구축 중…', en: 'Rebuilding…' }) : t({ ko: '폴더 트리 만들기', en: 'Build folders tree' })}
        </button>
      </div>
    );
  }
  const nodeOpts = hop?.options.filter((o) => o.kind === 'node') ?? [];
  const card = hop?.card as { docCountTotal?: number; byType?: Record<string, number>; landedRange?: { from: string; to: string } | null } | null;
  return (
    <div className="lake-tree ds-card ds-card--sm" aria-label={t({ ko: '선택지 트리', en: 'Option tree' })}>
      <div className="lake-tree__head">
        <select className="ds-control" value={treeKey} onChange={(e) => { setTreeKey(e.target.value); setAt('root'); onSelect(null); }} aria-label={t({ ko: '트리', en: 'Tree' })}>
          {trees.map((tr) => (
            <option key={tr.key} value={tr.key}>
              {tr.label} · {tr.nodes} {t({ ko: '노드', en: 'nodes' })} · {tr.documents} {t({ ko: '문서', en: 'docs' })}
            </option>
          ))}
        </select>
        <nav className="lake-tree__crumbs" aria-label={t({ ko: '경로', en: 'Path' })}>
          <button type="button" className={'link' + (at === 'root' ? ' is-current' : '')} onClick={() => go('root')}>
            {t({ ko: '루트', en: 'root' })}
          </button>
          {hop?.ancestors.filter((a) => a.path !== '').map((a) => (
            <span key={a.id}>
              {' / '}
              <button type="button" className="link" onClick={() => go(a.id)}>{a.label}</button>
            </span>
          ))}
          {hop && hop.at.kind === 'node' && hop.at.path !== '' ? <span>{' / '}<strong>{hop.at.label}</strong></span> : null}
        </nav>
        {card ? (
          <span className="muted small lake-tree__card">
            {String(card.docCountTotal ?? 0)} {t({ ko: '문서', en: 'docs' })}
            {card.byType ? ' · ' + Object.entries(card.byType).map(([k, v]) => k + ' ' + String(v)).join(', ') : ''}
            {card.landedRange ? ' · ' + shortDate(card.landedRange.from).slice(0, 10) + ' ~ ' + shortDate(card.landedRange.to).slice(0, 10) : ''}
          </span>
        ) : null}
        <button type="button" className="ds-button ds-button--ghost ds-button--sm" onClick={() => void rebuild()} disabled={busy} title={t({ ko: '폴더 트리를 sourceRef 로 다시 붙이고 카드가 없는 문서에 카드를 만듭니다', en: 'Re-attach folders from sourceRef and backfill missing cards' })}>
          {busy ? '…' : t({ ko: '재구축', en: 'Rebuild' })}
        </button>
      </div>
      {error ? <p className="status small" role="status">{error}</p> : null}
      <div className="lake-tree__children">
        {nodeOpts.length === 0 ? (
          <span className="muted small">{t({ ko: '하위 노드 없음 — 아래 목록이 이 노드의 문서입니다.', en: 'No child nodes — the list below is this node\u2019s documents.' })}</span>
        ) : (
          nodeOpts.map((o) => (
            <button
              key={o.id}
              type="button"
              className={'lake-tree__chip' + (selectedNode === o.id ? ' is-selected' : '')}
              onClick={() => go(o.id)}
              title={o.node?.path}
            >
              <span className="lake-tree__chip-label">{o.label}</span>
              <span className="lake-tree__chip-count">{o.node?.docCountTotal ?? 0}</span>
            </button>
          ))
        )}
        {hop && hop.totals.nodes > nodeOpts.length ? (
          <span className="muted small">+{hop.totals.nodes - nodeOpts.length} {t({ ko: '더', en: 'more' })}</span>
        ) : null}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <div className="lake-stat">
      <span className="lake-stat__value">{value}</span>
      <span className="lake-stat__label">{label}</span>
      {hint ? <span className="lake-stat__hint muted small">{hint}</span> : null}
    </div>
  );
}

function BaselineBadge({ b }: { b: LakeDocument['baseline'] }): JSX.Element {
  const t = useT();
  if (!b) return <span className="ds-badge ds-badge--neutral">{t({ ko: '저장됨 · 추출 미확인', en: 'Saved · extraction unknown' })}</span>;
  if (b.status === 'succeeded') {
    const pages = b.pageCount !== null && b.pageCount !== undefined ? String(b.pageCount) + 'p' : '';
    const scanned = b.hasTextLayer === false;
    return (
      <span className={'ds-badge ' + (scanned ? 'ds-badge--warning' : 'ds-badge--success')} title={scanned ? t({ ko: '텍스트 레이어 없음(스캔본) — OCR 은 외부', en: 'No text layer (scan) — OCR is external' }) : ''}>
        {scanned ? t({ ko: '읽을 텍스트 없음', en: 'No readable text' }) : t({ ko: '본문 검색 가능', en: 'Text searchable' })} {pages} {b.truncated ? t({ ko: '· 일부 추출', en: '· partial' }) : ''}
      </span>
    );
  }
  if (b.status === 'skipped') return <span className="ds-badge ds-badge--neutral" title={b.reason ?? ''}>{b.reason === 'baseline_resource_limit' ? t({ ko: '저장됨 · 별도 추출 필요', en: 'Saved · separate extraction needed' }) : b.reason === 'no_baseline_extractor' ? t({ ko: '저장됨 · 본문 추출 미지원', en: 'Saved · extraction unsupported' }) : t({ ko: '저장됨 · 추출 건너뜀', en: 'Saved · extraction skipped' })}</span>;
  if (b.status === 'failed') return <span className="ds-badge ds-badge--danger" title={b.error ?? ''}>{b.nextAttemptAt ? t({ ko: '추출 실패 · 재시도 예정', en: 'Failed · retry scheduled' }) : t({ ko: '추출 실패 · 확인 필요', en: 'Failed · needs attention' })}</span>;
  return <span className="ds-badge ds-badge--accent">{b.status === 'running' ? t({ ko: '추출 중', en: 'Extracting' }) : t({ ko: '추출 대기', en: 'Queued' })}</span>;
}

interface DetailState {
  summary: Array<{ kind: string; producer: string; producerVersion: string; count: number; updatedAt: string }> | null;
  jobs: Array<{ id: string; kind: string; status: string; attempt: number; worker: string | null; error: string | null; finishedAt: string | null }> | null;
  preview: string | null;
  error: string | null;
  busy: boolean;
}

function DocDetail({ doc, onClose, onChanged }: { doc: LakeDocument; onClose: () => void; onChanged: () => void }): JSX.Element {
  const t = useT();
  const [state, setState] = useState<DetailState>({ summary: null, jobs: null, preview: null, error: null, busy: false });
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, error: null }));
    const [sum, jobs, preview] = await Promise.allSettled([
      api.docDescriptorSummary(doc.id),
      api.docJobs(doc.id),
      api.docDescriptors(doc.id, { kind: 'text.page', page: 1 }),
    ]);
    const firstErr = [sum, jobs].find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
    let text: string | null = null;
    if (preview.status === 'fulfilled') {
      const p = preview.value.descriptors[0]?.payload as { text?: string } | undefined;
      text = p?.text ? p.text.slice(0, 1500) : null;
    }
    setState({
      summary: sum.status === 'fulfilled' ? sum.value.summary : null,
      jobs: jobs.status === 'fulfilled' ? jobs.value.jobs : null,
      preview: text,
      error: firstErr ? msgOf(firstErr.reason, t({ ko: '상세를 불러오지 못했습니다.', en: 'Could not load details.' })) : null,
      busy: false,
    });
  }, [doc.id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const rerun = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await api.rerunBaseline(doc.id);
      await load();
      onChanged();
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: msgOf(err, t({ ko: '재실행 실패', en: 'Rerun failed' })) }));
    }
  }, [doc.id, load, onChanged, t]);

  const copyHash = useCallback(() => {
    if (!doc.contentHash) return;
    void navigator.clipboard?.writeText(doc.contentHash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  }, [doc.contentHash]);

  const metaText = useMemo(() => {
    const keys = Object.keys(doc.metadata ?? {});
    return keys.length ? JSON.stringify(doc.metadata, null, 2) : '';
  }, [doc.metadata]);

  return (
    <aside className="lake-detail ds-card ds-card--raised ds-card--md" aria-label={t({ ko: '문서 상세', en: 'Document details' })}>
      <div className="lake-detail__head">
        <h2 title={doc.originalFilename}>{doc.title}</h2>
        <button type="button" className="ds-button ds-button--ghost ds-button--sm" onClick={onClose} aria-label={t({ ko: '닫기', en: 'Close' })}>
          ✕
        </button>
      </div>
      <dl className="lake-detail__facts">
        <dt>ID</dt>
        <dd>
          <code>{doc.id}</code>
        </dd>
        <dt>{t({ ko: '파일', en: 'File' })}</dt>
        <dd>
          {doc.originalFilename} · {doc.documentType.toUpperCase()} · {formatBytes(doc.fileSize)}
        </dd>
        <dt>{t({ ko: '해시', en: 'Hash' })}</dt>
        <dd>
          <code title={doc.contentHash ?? ''}>{shortHash(doc.contentHash)}…</code>{' '}
          <button type="button" className="link" onClick={copyHash}>
            {copied ? t({ ko: '복사됨', en: 'Copied' }) : t({ ko: '복사', en: 'Copy' })}
          </button>
        </dd>
        <dt>{t({ ko: '버전', en: 'Version' })}</dt>
        <dd>
          v{doc.version}
          {doc.lineageId && doc.lineageId !== doc.id ? (
            <>
              {' · '}
              {t({ ko: '계보', en: 'lineage' })} <code>{doc.lineageId}</code>
            </>
          ) : null}
        </dd>
        <dt>{t({ ko: '출처', en: 'Source' })}</dt>
        <dd>
          {doc.source ? <code>{doc.source}</code> : '—'} {doc.sourceRef}
        </dd>
        <dt>{t({ ko: '묶음', en: 'Batch' })}</dt>
        <dd>{doc.ingestBatch ? <code>{doc.ingestBatch}</code> : '—'}</dd>
        {doc.project ? (
          <>
            <dt>{t({ ko: '자료함', en: 'Folder' })}</dt>
            <dd>
              {doc.project.code} · {doc.project.name}
            </dd>
          </>
        ) : null}
        <dt>{t({ ko: '올린 사람', en: 'Owner' })}</dt>
        <dd>{doc.owner?.email ?? '—'}</dd>
        <dt>{t({ ko: '들어온 때', en: 'Landed' })}</dt>
        <dd>{shortDate(doc.createdAt)}</dd>
      </dl>
      {metaText ? (
        <details className="lake-detail__meta" open>
          <summary>{t({ ko: '메타데이터', en: 'Metadata' })}</summary>
          <pre>{metaText}</pre>
        </details>
      ) : null}
      <div className="lake-detail__actions">
        {doc.bytesPresent === false ? (
          doc.externalUri ? (
            <a className="ds-button ds-button--secondary ds-button--sm" href={doc.externalUri} target="_blank" rel="noreferrer">
              {t({ ko: '원본 열기(외부)', en: 'Open original (external)' })}
            </a>
          ) : (
            <span className="muted small">{t({ ko: '연결 문서 — 바이트는 원본에만 있습니다', en: 'Linked document — bytes live at the source' })}</span>
          )
        ) : (
          <a className="ds-button ds-button--secondary ds-button--sm" href={api.rawDocUrl(doc.id)}>
            {t({ ko: '원본 내려받기', en: 'Download bytes' })}
          </a>
        )}
        {doc.bytesPresent !== false && doc.externalUri ? (
          <a className="ds-button ds-button--ghost ds-button--sm" href={doc.externalUri} target="_blank" rel="noreferrer">
            {t({ ko: '원본 위치', en: 'Source location' })}
          </a>
        ) : null}
        <button type="button" className="ds-button ds-button--secondary ds-button--sm" onClick={() => void rerun()} disabled={state.busy}>
          {state.busy ? t({ ko: '실행 중…', en: 'Running…' }) : t({ ko: '기본 추출 재실행', en: 'Rerun baseline' })}
        </button>
      </div>
      {state.error ? (
        <p className="status err" role="alert">
          {state.error}
        </p>
      ) : null}
      <h3>{t({ ko: '서술자', en: 'Descriptors' })}</h3>
      {state.summary === null ? (
        <p className="muted small">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
      ) : state.summary.length === 0 ? (
        <p className="muted small">{t({ ko: '아직 없음', en: 'None yet' })}</p>
      ) : (
        <table className="lake-detail__table">
          <thead>
            <tr>
              <th>kind</th>
              <th>producer</th>
              <th>{t({ ko: '행', en: 'rows' })}</th>
              <th>{t({ ko: '갱신', en: 'updated' })}</th>
            </tr>
          </thead>
          <tbody>
            {state.summary.map((s) => (
              <tr key={s.kind + '|' + s.producer}>
                <td>
                  <code>{s.kind}</code>
                </td>
                <td className="muted small">
                  {s.producer}@{s.producerVersion}
                </td>
                <td>{s.count}</td>
                <td className="muted small">{shortDate(s.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {state.preview ? (
        <details className="lake-detail__preview" open>
          <summary>{t({ ko: 'text.page 1 미리보기', en: 'text.page 1 preview' })}</summary>
          <pre>{state.preview}</pre>
        </details>
      ) : null}
      <h3>{t({ ko: '처리 원장', en: 'Processing ledger' })}</h3>
      {state.jobs === null ? (
        <p className="muted small">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
      ) : state.jobs.length === 0 ? (
        <p className="muted small">{t({ ko: '기록 없음', en: 'No jobs' })}</p>
      ) : (
        <ul className="lake-detail__jobs">
          {state.jobs.map((j) => (
            <li key={j.id}>
              <code>{j.kind}</code> <span className={'ds-badge ' + (j.status === 'succeeded' ? 'ds-badge--success' : j.status === 'failed' ? 'ds-badge--danger' : 'ds-badge--neutral')}>{j.status}</span>{' '}
              <span className="muted small">
                #{j.attempt} {j.worker ?? ''} {shortDate(j.finishedAt)}
              </span>
              {j.error ? <div className="status err small">{j.error}</div> : null}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
